import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Users, FileText, LayoutList, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { Logo } from "../../components/Logo";
import { Seo } from "../../components/Seo";
import AdminLeads from "./AdminLeads";
import AdminPosts from "./AdminPosts";
import AdminContent from "./AdminContent";
import AdminSettings from "./AdminSettings";

const TABS = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "posts", label: "Blog Posts", icon: FileText },
  { id: "content", label: "Services & Areas", icon: LayoutList },
  { id: "settings", label: "Settings & Pricing", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-bone"><Loader2 className="w-8 h-8 animate-spin text-leaf" data-testid="admin-loading" /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-bone">
      <Seo title="Admin Dashboard | Glow Green" description="Glow Green site administration." />
      <header className="bg-forest sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-xs hidden sm:block">{user.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:border-leaf hover:text-leaf transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                tab === id ? "bg-forest text-white" : "bg-white border border-forest/15 text-forest hover:border-leaf"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        {tab === "leads" && <AdminLeads />}
        {tab === "posts" && <AdminPosts />}
        {tab === "content" && <AdminContent />}
        {tab === "settings" && <AdminSettings />}
      </div>
    </div>
  );
}
